import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Send, 
  Sparkles, 
  Loader2,
  Lock,
  Star,
  Wand2,
  Crown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

// Motivational quotes for manifestation
const quotes = [
  "The universe rewards those who prepare.",
  "Luck is when preparation meets opportunity.",
  "Manifest your reality with intention.",
  "Trust the process, embrace the journey.",
  "Your energy attracts your outcomes.",
  "Believe in the magic of new beginnings.",
  "Align your thoughts with your desires.",
  "Fortune favors the bold spirit.",
  "Every number tells a story.",
  "Dream it. Believe it. Manifest it."
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(quotes[Math.floor(Math.random() * quotes.length)]);
  const [weeklyUsage, setWeeklyUsage] = useState(0);
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);

  const isPro = user?.email === "ai.manifest.official@gmail.com" || user?.subscription_tier === "pro" || user?.subscription_tier === "premium";
  const hasReachedFreeLimit = !isPro && weeklyUsage >= 1;

  // Show greeting message on initial load - only once per session
  useEffect(() => {
    if (chatHistory.length === 0) {
      setChatHistory([{
        role: "assistant",
        content: `Hello Millionaire 👋✨

Welcome to your command center! I'm AURA, your AI guide to lottery insights.

I can help you with predictions, strategy tips, hot/cold number analysis, or just answer questions about how our AI works. What would you like to explore today?`,
        isGreeting: true
      }]);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote(quotes[Math.floor(Math.random() * quotes.length)]);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchWeeklyUsage();
  }, [token]);

  const fetchWeeklyUsage = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/predictions/weekly-usage`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setWeeklyUsage(response.data.games_generated || 0);
    } catch (error) {
      setWeeklyUsage(user?.predictions_this_week || 0);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    setMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  };

  // Enhanced response formatting - ChatGPT style
  const formatResponse = (text) => {
    // Handle the greeting message specially
    if (text.includes("Hello Millionaire")) {
      const lines = text.split('\n').filter(l => l.trim());
      return (
        <div className="space-y-3">
          {lines.map((line, idx) => {
            if (line.includes("Hello Millionaire")) {
              return <p key={idx} className="text-lg font-medium text-white">{line}</p>;
            }
            return <p key={idx} className="text-aura-text-secondary leading-relaxed">{line}</p>;
          })}
        </div>
      );
    }

    // Highlight key terms
    const highlightText = (text) => {
      const patterns = [
        { regex: /\*\*([^*]+)\*\*/g, replacement: '<strong class="text-white font-semibold">$1</strong>' },
        { regex: /(Hot Numbers?|hot numbers?)/gi, class: 'text-orange-400 font-medium' },
        { regex: /(Cold Numbers?|cold numbers?)/gi, class: 'text-blue-400 font-medium' },
        { regex: /(Overdue|overdue)/gi, class: 'text-purple-400 font-medium' },
        { regex: /(AURA PRO|PRO)/g, class: 'text-aura-gold font-semibold' },
        { regex: /(Powerball)/gi, class: 'text-red-400 font-medium' },
        { regex: /(\$\d+\.?\d*)/g, class: 'text-aura-gold font-medium' },
      ];

      let result = text;
      // Handle bold markdown first
      result = result.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
      
      patterns.slice(1).forEach(({ regex, class: className }) => {
        result = result.replace(regex, `<span class="${className}">$1</span>`);
      });
      return <span dangerouslySetInnerHTML={{ __html: result }} />;
    };

    // Check if response contains lottery numbers (prediction)
    const hasNumbers = /\d+[,\s]+\d+[,\s]+\d+/.test(text) || /Your Numbers?:/i.test(text);
    
    if (hasNumbers) {
      // Split into paragraphs
      const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
      
      return (
        <div className="space-y-4">
          {paragraphs.map((para, idx) => {
            const trimmed = para.trim();
            
            // Check if this paragraph contains numbers
            if (/Your Numbers?:/i.test(trimmed) || /^\*\*Your Numbers?\*\*/.test(trimmed)) {
              return (
                <div key={idx} className="p-4 rounded-xl bg-gradient-to-r from-aura-emerald/15 to-transparent border border-aura-emerald/25">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-aura-emerald" />
                    <span className="text-aura-emerald font-medium text-sm">Generated Numbers</span>
                  </div>
                  <p className="font-mono text-white text-lg tracking-wider">{highlightText(trimmed)}</p>
                </div>
              );
            }
            
            // Check if it's a disclaimer/note
            if (/disclaimer|guarantee|random|statistical/i.test(trimmed) && trimmed.length < 200) {
              return (
                <p key={idx} className="text-aura-text-muted text-sm italic border-l-2 border-white/10 pl-3">
                  {trimmed}
                </p>
              );
            }
            
            // Regular paragraph
            return (
              <p key={idx} className="text-aura-text-secondary leading-relaxed">
                {highlightText(trimmed)}
              </p>
            );
          })}
        </div>
      );
    }

    // For non-prediction responses, format as natural conversation
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
    
    return (
      <div className="space-y-3">
        {paragraphs.map((para, idx) => {
          const trimmed = para.trim();
          
          // Handle bullet lists
          if (trimmed.includes('\n-') || trimmed.includes('\n•') || /^[-•]\s/.test(trimmed)) {
            const lines = trimmed.split('\n');
            return (
              <div key={idx} className="space-y-2">
                {lines.map((line, lineIdx) => {
                  if (/^[-•]\s/.test(line)) {
                    return (
                      <p key={lineIdx} className="flex items-start gap-2 text-aura-text-secondary">
                        <span className="text-aura-emerald mt-1">•</span>
                        <span>{highlightText(line.replace(/^[-•]\s*/, ''))}</span>
                      </p>
                    );
                  }
                  return <p key={lineIdx} className="text-aura-text-secondary">{highlightText(line)}</p>;
                })}
              </div>
            );
          }
          
          // Regular paragraph
          return (
            <p key={idx} className="text-aura-text-secondary leading-relaxed">
              {highlightText(trimmed)}
            </p>
          );
        })}
      </div>
    );
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || loading) return;

    const userMessage = message.trim();

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    setMessage("");
    setChatHistory(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/aura-chat`,
        { message: userMessage },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      
      const responseText = response.data.response;
      
      // Check if backend returned limit warning
      if (responseText === "LIMIT_REACHED" || responseText === "WEEKLY_LIMIT_REACHED") {
        setChatHistory(prev => [...prev, { 
          role: "assistant", 
          content: "weekly_limit",
          isLimitWarning: true,
          limitType: "weekly"
        }]);
        setWeeklyUsage(1);
      } else if (responseText === "DAILY_LIMIT_REACHED") {
        setChatHistory(prev => [...prev, { 
          role: "assistant", 
          content: "daily_limit",
          isLimitWarning: true,
          limitType: "daily"
        }]);
      } else {
        setChatHistory(prev => [...prev, { 
          role: "assistant", 
          content: responseText
        }]);
        
        // Refresh usage if it was a prediction request
        const isPredictionRequest = /predict|number|lottery|lotto|powerball|generate|game|lucky/i.test(userMessage);
        if (isPredictionRequest) {
          fetchWeeklyUsage();
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setChatHistory(prev => [...prev, { 
          role: "assistant", 
          content: "weekly_limit",
          isLimitWarning: true,
          limitType: "weekly"
        }]);
        setWeeklyUsage(1);
      } else {
        toast.error("Failed to get response");
        setChatHistory(prev => [...prev, { 
          role: "assistant", 
          content: "I'm having a brief connection issue. Please try again in a moment." 
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const quickPrompts = [
    { text: "What are hot numbers?", icon: "🔥" },
    { text: "Give me lucky numbers", icon: "🎱" },
    { text: "Tatts Lotto strategy", icon: "📊" },
    { text: "How does AURA work?", icon: "✨" },
  ];

  return (
    <DashboardLayout currentPage="dashboard">
        {/* Dashboard Content */}
        <div className="flex-1 p-4 md:p-6 flex flex-col items-center relative overflow-hidden">
          <div className="noise-overlay absolute inset-0 pointer-events-none" />
          
          {/* Motivational Quote Block */}
          <div className="w-full max-w-3xl mb-6">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-aura-emerald/10 via-aura-gold/5 to-aura-emerald/10 border border-aura-emerald/20 p-5 shadow-xl">
              <div className="absolute top-0 right-0 w-32 h-32 bg-aura-gold/5 rounded-full blur-3xl" />
              <div className="relative flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-aura-gold/10 flex items-center justify-center flex-shrink-0">
                  <Wand2 className="w-5 h-5 text-aura-gold" />
                </div>
                <div>
                  <p className="text-xs text-aura-gold font-medium uppercase tracking-wider mb-2">Daily Manifestation</p>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currentQuote}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.5 }}
                      className="text-white font-medium text-lg leading-relaxed"
                    >
                      "{currentQuote}"
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          {/* Free Limit Warning */}
          {hasReachedFreeLimit && (
            <div className="w-full max-w-3xl mb-4">
              <div className="bg-gradient-to-r from-aura-gold/10 to-transparent border border-aura-gold/20 rounded-xl px-5 py-4 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-aura-gold" />
                  <div>
                    <p className="text-white font-medium text-sm">Weekly limit reached</p>
                    <p className="text-aura-text-muted text-xs">Upgrade for unlimited predictions</p>
                  </div>
                </div>
                <Button size="sm" className="btn-gold text-xs" onClick={() => navigate("/subscription")}>
                  <Crown className="w-3 h-3 mr-1" /> Unlock PRO
                </Button>
              </div>
            </div>
          )}
          
          {/* 8-Ball and Chat Container */}
          <div className="w-full max-w-3xl flex flex-col items-center">
            {/* 8-Ball */}
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-aura-emerald/20 scale-150 blur-3xl opacity-50" />
              <div className="eight-ball large aura-glow float-animation relative z-10">
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100px] h-[100px] bg-white rounded-full flex items-center justify-center text-4xl font-bold text-black font-heading shadow-inner">
                  8
                </span>
              </div>
            </div>

            {/* Ask AURA Title */}
            <div className="text-center mb-6">
              <h2 className="font-heading text-3xl font-bold text-white mb-2">Ask AURA</h2>
              <p className="text-aura-text-muted text-sm">Your AI guide to lottery insights</p>
            </div>

            {/* Chat History */}
              {chatHistory.length > 0 && (
                <div className="w-full mb-6">
                  <div className="rounded-2xl bg-aura-surface/50 backdrop-blur border border-white/5 p-4 md:p-6 max-h-[450px] overflow-y-auto space-y-4 shadow-2xl">
                    {chatHistory.map((msg, index) => (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.1, type: "spring", stiffness: 100 }}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {/* User message */}
                        {msg.role === 'user' && (
                          <div className="max-w-[85%] rounded-2xl px-5 py-4 shadow-lg bg-gradient-to-br from-aura-emerald/30 to-aura-emerald/10 text-white border border-aura-emerald/30">
                            <p className="text-white text-sm">{msg.content}</p>
                          </div>
                        )}
                        
                        {/* Limit Warning Card */}
                        {msg.role === 'assistant' && msg.isLimitWarning && (
                          <div className="max-w-[90%] rounded-2xl overflow-hidden shadow-lg border border-aura-gold/40 bg-gradient-to-br from-aura-gold/15 via-aura-gold/10 to-transparent">
                            <div className="px-5 py-4 border-b border-aura-gold/20 bg-aura-gold/10">
                              <div className="flex items-center gap-2">
                                <Crown className="w-5 h-5 text-aura-gold" />
                                <span className="font-medium text-aura-gold">
                                  {msg.limitType === "daily" ? "Daily Limit Reached" : "Conversation Limit Reached"}
                                </span>
                              </div>
                            </div>
                            <div className="px-5 py-5">
                              {msg.limitType === "daily" ? (
                                <>
                                  <p className="text-white mb-3">
                                    AURA conversation has ended for today.
                                  </p>
                                  <p className="text-aura-text-secondary text-sm">
                                    Rest, stay positive, and continue manifesting your success. I will see you again tomorrow.
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-white mb-3">
                                    AURA conversation limit reached for this week.
                                  </p>
                                  <p className="text-aura-text-secondary text-sm mb-4">
                                    Take time to reflect and keep manifesting your success. Your next AURA session will unlock next week.
                                  </p>
                                  <Button 
                                    className="btn-gold w-full"
                                    onClick={() => navigate("/subscription")}
                                  >
                                    <Crown className="w-4 h-4 mr-2" />
                                    Upgrade to AURA PRO
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Normal assistant message */}
                        {msg.role === 'assistant' && !msg.isLimitWarning && (
                          <div className="max-w-[90%] rounded-2xl px-5 py-4 shadow-lg bg-white/5 text-aura-text-secondary border border-white/10">
                            {!msg.isGreeting && (
                              <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/10">
                                <div className="w-6 h-6 rounded-full bg-aura-emerald/20 flex items-center justify-center">
                                  <Sparkles className="w-3 h-3 text-aura-emerald" />
                                </div>
                                <span className="text-xs text-aura-emerald font-medium">AURA</span>
                              </div>
                            )}
                            <div className="text-sm leading-relaxed">
                              {formatResponse(msg.content)}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ))}
                    {loading && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex justify-start"
                      >
                        <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4 shadow-lg">
                          <div className="flex items-center gap-3">
                            <div className="flex gap-1.5">
                              <span className="w-2 h-2 bg-aura-emerald rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <span className="w-2 h-2 bg-aura-emerald rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <span className="w-2 h-2 bg-aura-emerald rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                            <span className="text-aura-text-muted text-sm">AURA is thinking...</span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </div>
              )}

            {/* Chat Input - Wide and Comfortable */}
            <div className="w-full">
              <form onSubmit={handleSendMessage} className="relative">
                <div className="relative rounded-2xl bg-aura-surface/80 backdrop-blur border border-white/10 shadow-xl overflow-hidden focus-within:border-aura-emerald/30 transition-colors">
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={handleTextareaChange}
                    onKeyDown={handleKeyDown}
                    placeholder={hasReachedFreeLimit 
                      ? "Upgrade to PRO to continue your cosmic journey..." 
                      : "Ask about strategies, predictions, lucky numbers, or lottery tips..."}
                    className="w-full bg-transparent text-white placeholder-aura-text-muted px-5 py-4 pr-16 text-base resize-none focus:outline-none min-h-[56px] max-h-[150px]"
                    rows={1}
                    disabled={loading}
                    data-testid="chat-input"
                  />
                  <Button 
                    type="submit" 
                    size="icon"
                    className={`absolute right-3 bottom-3 rounded-xl w-10 h-10 ${
                      loading || !message.trim() 
                        ? 'bg-white/10 text-aura-text-muted cursor-not-allowed' 
                        : 'btn-primary shadow-lg'
                    }`}
                    disabled={loading || !message.trim()}
                    data-testid="chat-submit"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-aura-text-muted mt-2 text-center">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </form>

              {/* Quick Prompts */}
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                {quickPrompts.map((prompt) => (
                  <button
                    key={prompt.text}
                    onClick={() => setMessage(prompt.text)}
                    className="px-4 py-2 text-sm rounded-xl bg-white/5 border border-white/10 text-aura-text-secondary hover:bg-aura-emerald/10 hover:border-aura-emerald/20 hover:text-aura-emerald transition-all flex items-center gap-2 shadow-md"
                  >
                    <span>{prompt.icon}</span>
                    <span>{prompt.text}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Floating Responsible Gambling Notice */}
        <div className="fixed bottom-4 right-4 z-40 max-w-[300px] hidden md:block">
          <div className="p-4 rounded-xl bg-aura-surface/95 backdrop-blur border border-white/10 shadow-2xl">
            <p className="text-xs font-medium text-aura-emerald mb-1">Responsible Gambling</p>
            <p className="text-xs text-aura-text-muted leading-relaxed">
              Play responsibly. Set limits and never spend more than you can afford.
            </p>
          </div>
        </div>
    </DashboardLayout>
  );
};

export default Dashboard;
